/**
 * Choosing the zoom a photo opens at, and stepping it round in quarters.
 *
 * Every image the modal loads is padded (PADDING_RATIO) so the operator can zoom out past its
 * edge and place it against a background. That padding is a tool, not a default view: opening
 * at a zoom that merely FITS left a landscape webcam frame sitting as a strip inside a portrait
 * crop, with thick black bars above and below it before anyone had touched a control.
 */

export interface Size {
  width: number;
  height: number;
}

/**
 * The zoom at which the photo COVERS the crop window — no bars, at the cost of cropping the
 * long side.
 *
 * `baseZoom` is the zoom at which the unpadded photo exactly fits (ZOOM_FOR_ORIGINAL_FILL).
 * Covering needs the larger of the two scale factors instead of the smaller, and the ratio
 * between them is how far the photo's shape is from the crop's.
 *
 * Never returns less than `baseZoom`: filling may cut, but it must not put the bars back.
 */
export function zoomToFillCrop(image: Size, cropAspect: number, baseZoom: number): number {
  if (!image.width || !image.height || !cropAspect || !Number.isFinite(cropAspect)) {
    return baseZoom;
  }
  const imageAspect = image.width / image.height;
  const ratio = imageAspect / cropAspect;
  return baseZoom * Math.max(ratio, 1 / ratio);
}

/**
 * The next quarter turn from wherever the slider happens to be.
 *
 * Snapped, not added: the slider is for straightening a crooked photo, so a 7° correction
 * followed by "rotate 90°" must land on 90, not 97.
 *
 * Result lands in (-180, 180] — the range the Rotação slider actually spans. Returning 270
 * would put the handle past its own maximum, where it clamps and the next press does nothing.
 */
export function stepRotation(current: number, delta: number): number {
  const quarters = Math.round(current / 90) + Math.round(delta / 90);
  const degrees = ((quarters % 4) + 4) % 4 * 90;
  return degrees > 180 ? degrees - 360 : degrees;
}

/**
 * The zoom a photo should open at, for any of the capture modals.
 *
 * One rule, because each modal used to pick its own and they disagreed: an identity photo
 * opened with thick black bars while a vehicle opened cropped into.
 *
 *  - FILL for a face — round crops, and portrait or square ones. A landscape webcam frame
 *    fitted into a portrait identity crop is a strip between bars, and a circle with bars
 *    inside it just looks broken.
 *  - FIT for a vehicle or document — any landscape crop. Filling cuts the sides off, and the
 *    sides are where the rest of the car is, and often the plate.
 *
 * `baseZoom` is ZOOM_FOR_ORIGINAL_FILL: the zoom at which the unpadded photo exactly fits.
 */
export function openingZoomFor(input: {
  image: Size;
  cropAspect: number;
  cropShape: 'round' | 'rect';
  baseZoom: number;
}): number {
  const { image, cropAspect, cropShape, baseZoom } = input;
  const subjectIsAFace = cropShape === 'round' || cropAspect <= 1;
  return subjectIsAFace ? zoomToFillCrop(image, cropAspect, baseZoom) : baseZoom;
}
